import * as React from 'react';
import { ReactElement, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import {
  Grid,
  GridColumn,
  GridDataStateChangeEvent,
  GridSelectionChangeEvent,
  getSelectedState,
  GridSelectableMode,
  GridToolbar,
  GridCellProps,
  GridHeaderSelectionChangeEvent,
  GridCustomFilterCellProps,
  isColumnMenuSortActive,
  isColumnMenuFilterActive,
} from '@progress/kendo-react-grid';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import {
  CompositeFilterDescriptor,
  DataResult,
  process,
  State,
  FilterDescriptor,
} from '@progress/kendo-data-query';
import { getter } from '@progress/kendo-react-common';
import { setGroupIds } from '@progress/kendo-react-data-tools';
import { CSVLink } from 'react-csv';

import {
  LocalizationProvider,
  loadMessages,
  load,
  IntlProvider,
} from '@progress/kendo-react-intl';
import {
  ColumnMenu,
  CustomCell,
  HeaderCustomCell,
} from '../ColumnMenus/ColumnMenu';

import UpdateWghtDialog from '../../components/UpdateWghtDialog';
import SetThresholdDialog from '../../components/SetThresholdDialog';
import SplitButtonByAlert from '../SplitButton/SplitButtonByAlert';

import { ApiEnums } from '../../Interfaces/ApiEnums';

import { ApplicationState } from '../../store';
import {
  sendCommandResponse,
  setRefreshAlertsRequested,
  fetchAllAlertsByCommandStatus,
  fetchAllAlerts,
  clearAllAlerts,
} from '../../store/Alerts/AlertsActions';
import {
  CloseAlertRecords,
  SetWeightRecordToDefault,
  SetWeightRecordsToDefault,
  CleanWeightRecord,
} from '../../Interfaces/Alerts/CommandResponse';
import {
  WeightAlertRecord,
  WeightAlertGroupRecord,
  WeightAlertRecordExport,
} from '../../Interfaces/Alerts/AlertRecord';
import { selectionModes } from './GridRowSelectionMode';
import { LocaleInterface, locales } from './GridLocale';
import {
  CommandStatusCell,
  CommandStatusFilterCell,
} from './GridRowCommandStatus';
import { AlertTypeCell, AlertTypeFilterCell } from './GridRowAlertType';
import {
  convertAlertGridDataToCsvData,
  dateFormat,
  fetchJsonData,
  formatDateForExport,
  formatTimestampDate,
  getDateTimeLocaleString,
  getLocalDateTime,
} from '../../utils/utils';
import {
  CLEAN_WGT_RECORDS_API,
  CLOSE_ALERTS_API,
  SET_WGT_RECORDS_TO_DEFAULT_API,
} from '../../Interfaces/Alerts/Api';
import { saveAlertsGridState } from '../../store/UserSettings/UserSettingsActions';
import CleanWeightRecordDialog from '../CleanWeightRecordDialog';
import GenericSplitButton from '../GenericSplitButton/GenericSplitButton';
import { Button } from '@progress/kendo-react-buttons';
import { LoadingPanel } from '../LoadingPanel';
import StringResourceManager, { GetString } from '../StringResourceManager';
import esMessages from '../../utils/gridMessageKey.json';
loadMessages(esMessages, 'fr-CA');
import weekData from 'cldr-core/supplemental/weekData.json';
import currencyData from 'cldr-core/supplemental/currencyData.json';
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import caGregorian from 'cldr-dates-full/main/fr/ca-gregorian.json';
import numbers from 'cldr-numbers-full/main/fr/numbers.json';
import dateFields from 'cldr-dates-full/main/fr/dateFields.json';
import timeZones from 'cldr-dates-full/main/fr/timeZoneNames.json';
import { CustomDateFilterCell } from '../CustomDateFilterCell';
import AlertDetailsDialog from './AlertDetailsDialog';
import { Theme, Typography, createStyles, makeStyles } from '@material-ui/core';
import { ASSET } from '../../utils/constants';
load(
  likelySubtags,
  currencyData,
  weekData,
  caGregorian,
  numbers,
  dateFields,
  timeZones,
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dateTime: {
      textAlign: 'center',
      color: '#23496B',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: 35,
    },
  }),
);

const GRID_DATA_ITEM_KEY = 'ID';
const SELECTED_FIELD = 'selected';
const idGetter = getter(GRID_DATA_ITEM_KEY);

export const DateTimeCell = (props: GridCellProps) => {
  const dataItem = props.dataItem;
  if (dataItem?.CreatedOn && dataItem.CreatedOn != '0001-01-01T00:00:00') {
    return (
      <td style={{ fontSize: '0.9rem' }}>
        {getLocalDateTime(dataItem.CreatedOn)}
      </td>
    );
  }
  return <td></td>;
};

export const TimeStampCell = (props: GridCellProps) => {
  const dataItem = props.dataItem;
  if (dataItem?.Timestamp && dataItem.Timestamp != '0001-01-01T00:00:00') {
    return (
      <td style={{ fontSize: '0.9rem' }}>
        {formatTimestampDate(dataItem.Timestamp)}
      </td>
    );
  }
  return <td></td>;
};

export const fetchAlertByID = async (
  alertID: number,
  token: string | undefined,
) => {
  const alert = await fetchJsonData(`alertrecords/get/${alertID}`, token);
  return alert as WeightAlertRecord;
};

const fetchAllOpenAlertsAsync = async (
  alertStatus: ApiEnums.AlertStatus,
  token: string | undefined,
) => {
  return await fetchJsonData(
    `alertrecords/GetAllAlertsByAlertStatus/${alertStatus}`,
    token,
  );
};

const AlertsGrid: React.FC = (): ReactElement => {
  const classes = useStyles();

  const [alertToUpdate, setAlertToUpdate] =
    useState<WeightAlertGroupRecord>(Object);

  const token: string | undefined = useSelector(
    (state: ApplicationState) => state.userSessionReducer.sessionInfo?.Token,
  );

  const [isUpdateWghtDialogOpen, setUpdateWghtDialogOpen] = useState(false);
  const [isThresholdDialogOpen, setIsThresholdDialogOpen] = useState(false);
  const [isCleanWeightRecordDialogOpen, setIsCleanWeightRecordDialogOpen] =
    useState(false);

  const [globalSplitButtonDisabled, setGlobalSplitButtonDisabled] =
    useState<boolean>(true);

  const loading: boolean = useSelector(
    (state: ApplicationState) => state.alertsReducer.isLoadingAllAlerts,
  );

  const refresh: boolean = useSelector(
    (state: ApplicationState) => state.alertsReducer.shouldRefreshAlerts,
  );

  const [csvData, setCsvData] = useState<WeightAlertRecordExport[]>([]);
  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const [detailedAlertIDs, setDetailedAlertIDs] = useState<number[]>([]);
  const [alertDetailsModalVisible, setAlertDetailsModalVisible] =
    React.useState<boolean>(false);

  const dispatch: Dispatch<any> = useDispatch();

  const sendBatchCommandForSelectedRows = (command: ApiEnums.AlertCommand) => {
    setGlobalSplitButtonDisabled(true);
    handleAlertsGridBatchCommand(command);
  };

  const getAlertsExportData = (
    alertStatus: ApiEnums.AlertStatus,
    token: string | undefined,
  ) => {
    fetchAllOpenAlertsAsync(alertStatus, token)
      .then((alerts: WeightAlertRecord[]) => {
        let result: WeightAlertRecordExport[] = alerts.map(
          (alert: WeightAlertRecord) => convertAlertGridDataToCsvData(alert),
        );
        setCsvData(result);
      })
      .catch(error => {
        console.error(error);
        Promise.reject(error);
      });
  };

  const exportCsv = () => {
    getAlertsExportData(ApiEnums.AlertStatus.Opened, token);
  };

  const getCSVFileName = () => {
    return `alerts-${formatDateForExport(currentLocale.language)}`;
  };

  useEffect(() => {
    if (csvData.length > 0 && csvLinkRef?.current) {
      csvLinkRef.current.link.click();
      setCsvData([]);
    }
  }, [csvData]);

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const [selectedAlertRecords, setSelectedAlertRecords] = useState<
    WeightAlertGroupRecord[]
  >([]);

  const closeAlerts = (alerts: WeightAlertGroupRecord[]) => {
    let alertIDs: number[] = [];
    alerts.forEach(alert => {
      alert.AlertIDs.forEach(a => alertIDs.push(a));
    });

    let payload: CloseAlertRecords = {
      AlertIDs: alertIDs,
    };

    sendCommandResponse(dispatch, CLOSE_ALERTS_API, payload);
  };

  const cleanWeightRecords = (alerts: WeightAlertGroupRecord[]) => {
    let payload: CleanWeightRecord[] = [];
    alerts.forEach(alert => {
      alert.AlertIDs.forEach(id => {
        payload.push({
          AlertID: id,
          WeightCategory: alert.WeightCategory,
        });
      });
    });

    sendCommandResponse(dispatch, CLEAN_WGT_RECORDS_API, payload);
  };

  const setWeightRecordsToDefault = (alerts: WeightAlertGroupRecord[]) => {
    let data: SetWeightRecordToDefault[] = [];
    alerts.forEach(alert =>
      alert.AlertIDs.forEach(id => {
        data.push({
          AlertID: id,
          Weight: alert.ReferenceWeight,
          FixedWeight: alert.FixedWeight,
        });
      }),
    );

    let payload: SetWeightRecordsToDefault = {
      WeightRecords: data,
    };

    sendCommandResponse(dispatch, SET_WGT_RECORDS_TO_DEFAULT_API, payload);
  };

  const handleAlertsGridBatchCommand = (command: ApiEnums.AlertCommand) => {
    switch (command) {
      case ApiEnums.AlertCommand.CloseAlert:
        closeAlerts(selectedAlertRecords);
        break;
      case ApiEnums.AlertCommand.CleanWeightRecord:
        cleanWeightRecords(selectedAlertRecords);
        break;
      case ApiEnums.AlertCommand.SetWeightRecordToDefault:
        setWeightRecordsToDefault(selectedAlertRecords);
        break;
      case ApiEnums.AlertCommand.ApplyRecommended:
        let alerts: WeightAlertGroupRecord[] = selectedAlertRecords.filter(
          a => a.RecommendedCommand === ApiEnums.AlertCommand.CloseAlert,
        );
        if (alerts.length > 0) closeAlerts(alerts);

        alerts = selectedAlertRecords.filter(
          a => a.RecommendedCommand === ApiEnums.AlertCommand.CleanWeightRecord,
        );
        if (alerts.length > 0) cleanWeightRecords(alerts);

        alerts = selectedAlertRecords.filter(
          a =>
            a.RecommendedCommand ===
            ApiEnums.AlertCommand.SetWeightRecordToDefault,
        );
        if (alerts.length > 0) setWeightRecordsToDefault(alerts);
        break;
      default:
        break;
    }
  };

  const handleToggleDialog = async (
    alertRecord: WeightAlertGroupRecord,
    command: ApiEnums.AlertCommand,
  ) => {
    setAlertToUpdate(alertRecord);
    setDialogToOpen(command);
  };

  const setDialogToOpen = (command: ApiEnums.AlertCommand) => {
    switch (command) {
      case ApiEnums.AlertCommand.UpdateWeightRecord:
        setUpdateWghtDialogOpen(!isUpdateWghtDialogOpen);
        break;
      case ApiEnums.AlertCommand.SetWeightThreshold:
        setIsThresholdDialogOpen(!isThresholdDialogOpen);
        break;
      case ApiEnums.AlertCommand.CleanWeightRecord:
        setIsCleanWeightRecordDialogOpen(!isCleanWeightRecordDialogOpen);
        break;
      default:
        break;
    }
  };

  const handleButtonClick = async (
    alertRecord: WeightAlertGroupRecord,
    command: ApiEnums.AlertCommand,
  ) => {
    switch (command) {
      case ApiEnums.AlertCommand.CloseAlert:
        let closeAlertRecords: CloseAlertRecords = {
          AlertIDs: alertRecord.AlertIDs,
        };
        sendCommandResponse(dispatch, CLOSE_ALERTS_API, closeAlertRecords);
        break;
      case ApiEnums.AlertCommand.CleanWeightRecord:
        handleToggleDialog(alertRecord, command);
        break;
      case ApiEnums.AlertCommand.SetWeightRecordToDefault:
        // Silently update the weight record with the default (reference) weight
        let records: SetWeightRecordToDefault[] = [];
        alertRecord.AlertIDs.forEach(id => {
          records.push({
            AlertID: id,
            Weight: alertRecord.ReferenceWeight,
            FixedWeight: alertRecord.FixedWeight,
          });
        });

        let setWeightRecordToDefaultResponse: SetWeightRecordsToDefault = {
          WeightRecords: records,
        };
        sendCommandResponse(
          dispatch,
          SET_WGT_RECORDS_TO_DEFAULT_API,
          setWeightRecordToDefaultResponse,
        );
        break;
      case ApiEnums.AlertCommand.UpdateWeightRecord:
        handleToggleDialog(alertRecord, command);
        break;
      case ApiEnums.AlertCommand.SetWeightThreshold:
        handleToggleDialog(alertRecord, command);
        break;
      default:
    }
  };

  useEffect((): ReturnType<React.EffectCallback> => {
    if (refresh) {
      clearAllAlerts(dispatch);
      setAlertToUpdate(Object);
      fetchAlerts();
      setRefreshAlertsRequested(dispatch, false);
    }

    return (): void => {};
  }, [refresh]);

  const fetchAlerts = () => {
    if (dataState.filter?.filters !== undefined) {
      const filters: (CompositeFilterDescriptor | FilterDescriptor)[] =
        dataState.filter?.filters;
      const commandStatusFilter: any = filters.find(
        (x: any) => x?.field == 'CommandStatus',
      );
      if (commandStatusFilter !== undefined)
        fetchAllAlertsByCommandStatus(dispatch, commandStatusFilter?.value);
      else fetchAllAlerts(dispatch);
    } else fetchAllAlerts(dispatch);
  };

  useEffect(() => {
    if (dataState !== undefined) {
      setDataResult(processWithGroups(alerts, dataState));
    }

    return () => {
      clearAllAlerts(dispatch);
    };
  }, []);

  const [selectionMode, setSelectionMode] = React.useState<
    GridSelectableMode | undefined
  >(selectionModes[1].value);

  const [selectedState, setSelectedState] = React.useState<{
    [id: string]: boolean | number[];
  }>({});

  const onSelectionChange = (event: GridSelectionChangeEvent) => {
    const newSelectedState = getSelectedState({
      event,
      selectedState: selectedState,
      dataItemKey: GRID_DATA_ITEM_KEY,
    });
    setSelectedState(newSelectedState);
  };

  const onHeaderSelectionChange = React.useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event.syntheticEvent.target;
      const checked = checkboxElement.checked;
      const newSelectedState: any = {};

      event.dataItems.forEach(item => {
        newSelectedState[idGetter(item)] = checked;
      });
      setSelectedState(newSelectedState);
    },
    [],
  );

  useEffect((): ReturnType<React.EffectCallback> => {
    let selectedRecords: WeightAlertGroupRecord[] = [];

    Object.keys(selectedState).map(function (key) {
      if (selectedState[key]) {
        const selectedAlert = alerts.find(
          alert =>
            alert.ID.toString() === key &&
            alert.CommandStatus === ApiEnums.CommandStatus.New,
        );
        if (selectedAlert) {
          selectedRecords.push(selectedAlert);
        }
      }
    });

    setSelectedAlertRecords(selectedRecords);
    setGlobalSplitButtonDisabled(selectedRecords.length <= 0);
  }, [selectedState]);

  const processWithGroups = (data: any[], dataState: State): DataResult => {
    const newDataState: DataResult = process(data, dataState);
    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };

  const alerts: WeightAlertGroupRecord[] = useSelector(
    (state: ApplicationState) => state.alertsReducer.allAlerts,
  );

  const dataState: State = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.alertsGridDataState,
  );

  const [dataResult, setDataResult] = React.useState<DataResult>(
    processWithGroups(alerts, dataState),
  );

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataResult(processWithGroups(alerts, event.dataState));
    saveAlertsGridState(dispatch, event.dataState);
  };

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const SupportedCommandsCellWrapper = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    if (dataItem?.Barcode) {
      return (
        <td>
          <SplitButtonByAlert
            alertRecord={dataItem}
            handleDialog={handleToggleDialog}
            handleButtonClick={handleButtonClick}
          />
        </td>
      );
    }
    return <td></td>;
  };

  const dateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="CreatedOn"
      />
    );
  };

  const timestampFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="Timestamp"
      />
    );
  };

  const showAlertDetailsForm = (a: WeightAlertGroupRecord) => {
    setDetailedAlertIDs(a.AlertIDs);
    setAlertDetailsModalVisible(true);
  };

  const AlertDetailsCell = (props: GridCellProps) => {
    const dataItem = props.dataItem as WeightAlertGroupRecord;
    if (!dataItem.AlertIDs) return null;

    return (
      <td>
        <Button
          themeColor={'primary'}
          type="button"
          //disabled={dataItem.Status <= ApiEnums.DeviceStatus.Ok}
          onClick={() => showAlertDetailsForm(dataItem)}
        >
          <StringResourceManager stringKey="VIEW_DETAILS" variant="body1" />
        </Button>
      </td>
    );
  };

  const columnProps = (field: string) => {
    return {
      field: field,
      columnMenu: ColumnMenu,
      headerClassName: isColumnActive(field, dataState) ? 'active' : '',
    };
  };

  const isColumnActive = (field: string, dataState: State) => {
    return (
      isColumnMenuFilterActive(field, dataState.filter) ||
      isColumnMenuSortActive(field, dataState.sort)
    );
  };

  return (
    <React.Fragment>
      <UpdateWghtDialog
        title={GetString('UPDATE_WGT_RECORD_TITLE')}
        isDialogOpened={isUpdateWghtDialogOpen}
        handleCloseDialog={() => setUpdateWghtDialogOpen(false)}
        alertToUpdate={alertToUpdate}
      />
      <SetThresholdDialog
        title={GetString('SET_THRESHOLD_TITLE')}
        isDialogOpened={isThresholdDialogOpen}
        handleCloseDialog={() => setIsThresholdDialogOpen(false)}
        alertToUpdate={alertToUpdate}
      />
      <CleanWeightRecordDialog
        title={GetString('CLEAN_WEIGHT_RECORD')}
        isDialogOpened={isCleanWeightRecordDialogOpen}
        handleCloseDialog={() => setIsCleanWeightRecordDialogOpen(false)}
        alertToUpdate={alertToUpdate}
      />

      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <div data-testid="alerts-grid">
            <CSVLink
              data={csvData}
              ref={csvLinkRef}
              filename={getCSVFileName() + '.csv'}
            />
            {loading && LoadingPanel}
            <Grid
              style={{ height: '73vh' }}
              sortable={true}
              filterable={true}
              groupable={true}
              reorderable={true}
              fixedScroll={false}
              pageable={{
                buttonCount: 4,
                pageSizes: [10, 20, 50, 100, 500, 1000],
              }}
              data={processWithGroups(
                alerts.map(item => ({
                  ...item,
                  [SELECTED_FIELD]:
                    selectedState[idGetter(item)] &&
                    item.CommandStatus === ApiEnums.CommandStatus.New,
                })),
                dataState,
              )}
              {...dataState}
              onDataStateChange={dataStateChange}
              resizable={true}
              dataItemKey={GRID_DATA_ITEM_KEY}
              selectable={{
                enabled: true,
                drag: true,
                cell: false,
                mode: selectionMode,
              }}
              onSelectionChange={onSelectionChange}
              onHeaderSelectionChange={onHeaderSelectionChange}
              cells={{
                headerCell: HeaderCustomCell,
                data: CustomCell,
              }}
            >
              <GridToolbar>
                &nbsp;
                <GenericSplitButton
                  text={GetString('SELECT_COMMAND')}
                  supportedCommands={[
                    ApiEnums.AlertCommand.ApplyRecommended,
                    ApiEnums.AlertCommand.CleanWeightRecord,
                    ApiEnums.AlertCommand.SetWeightRecordToDefault,
                    ApiEnums.AlertCommand.CloseAlert,
                  ]}
                  recommendedCommand={ApiEnums.AlertCommand.ApplyRecommended}
                  disabled={globalSplitButtonDisabled}
                  sendCommand={sendBatchCommandForSelectedRows}
                />
                &nbsp;
                <Button onClick={exportCsv}>
                  {GetString('EXPORT_TO_CSV')}
                </Button>
                &nbsp;
              </GridToolbar>
              <GridColumn
                field={SELECTED_FIELD}
                width="70px"
                filterable={false}
                headerSelectionValue={
                  alerts.findIndex(item => !selectedState[idGetter(item)]) ===
                  -1
                }
                columnType="checkbox"
              />
              <GridColumn
                {...columnProps('ItemDesc')}
                title={GetString('ITEM_TITLE')}
                width="200px"
                locked={true}
                columnMenu={ColumnMenu}
                filterable={false}
              />
              <GridColumn
                field="AlertType"
                title={GetString('ALERT_TYPE_TITLE')}
                width="205px"
                cells={{ data: AlertTypeCell, filterCell: AlertTypeFilterCell }}
              />
              <GridColumn
                field="CommandStatus"
                title={GetString('STATUS_TITLE')}
                width="180px"
                cells={{
                  data: CommandStatusCell,
                  filterCell: CommandStatusFilterCell,
                }}
              />
              <GridColumn
                field="SupportedCommands"
                title={GetString('COMMANDS_TITLE')}
                width="175px"
                filterable={false}
                cells={{ data: SupportedCommandsCellWrapper }}
              />
              <GridColumn
                title={GetString('DETAILS')}
                width="175px"
                cells={{ data: AlertDetailsCell }}
                filterable={false}
              />
              <GridColumn
                field="Count"
                title={GetString('ALERT_COUNT_TITLE')}
                width="185px"
                filter="numeric"
              />
              <GridColumn
                field={ASSET.FIELD_STORE_NAME}
                title={GetString('STORE_FIRST_REPORTED_TITLE')}
                width="175px"
              />
              <GridColumn
                field="CreatedOn"
                title={GetString('CREATED_ON_TITLE')}
                width="220px"
                cells={{ data: DateTimeCell, filterCell: dateFilterCell }}
              />
              <GridColumn
                field="Timestamp"
                title={GetString('TIMESTAMP')}
                width="220px"
                cells={{ data: TimeStampCell, filterCell: timestampFilterCell }}
              />
            </Grid>
          </div>
          <Typography color="textSecondary" className={classes.dateTime}>
            {GetString('LAST_REFRESH_DATETIME')}
            {dateFormat(currentLocale.language)}
          </Typography>
          {alertDetailsModalVisible && (
            <AlertDetailsDialog
              handleCloseDialog={() => setAlertDetailsModalVisible(false)}
              alertIDs={detailedAlertIDs}
            />
          )}
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default AlertsGrid;
